import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { School } from '../../../models/school/school';
import SchoolCard from './SchoolCard';

interface SchoolsSectionProps {
  title: string;
  description: string;
  schools: School[];
  showViewAllButton?: boolean;
  viewAllButtonText?: string;
  viewAllButtonLink?: string;
  limit?: number;
  loading?: boolean;
  showEnrollmentProgress?: boolean;
}

const SchoolsSection: React.FC<SchoolsSectionProps> = ({
  title,
  description,
  schools,
  showViewAllButton = true,
  viewAllButtonText,
  viewAllButtonLink = '/schools',
  limit,
  loading = false,
  showEnrollmentProgress = true
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Apply limit if specified
  const displayedSchools = limit ? schools.slice(0, limit) : schools;

  // Loading skeleton
  if (loading) {
    return (
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header skeleton */}
          <div className="text-center mb-16">
            <div className="w-16 h-16 bg-gray-200 animate-pulse rounded-full mx-auto mb-6"></div>
            <div className="h-12 bg-gray-200 animate-pulse rounded mx-auto mb-4 max-w-md"></div>
            <div className="h-6 bg-gray-200 animate-pulse rounded mx-auto max-w-2xl"></div>
          </div>

          {/* Cards skeleton */}
          <div className="flex justify-center mb-16">
            <div className="w-full max-w-4xl bg-gray-200 animate-pulse rounded-3xl h-96"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white text-black py-20" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-black text-white rounded-full mb-6">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
            </svg>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            {title}
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </div>

        {/* Schools Display */}
        {displayedSchools.length > 0 ? (
          <div className="space-y-8 mb-12">
            {displayedSchools.map((school) => (
              <div key={school.id} className="flex justify-center">
                <div className="w-full max-w-4xl">
                  <SchoolCard
                    school={school}
                    showEnrollmentProgress={showEnrollmentProgress}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 text-gray-400 rounded-full mb-6">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('pages.schools.noSchools.title', 'No Schools Available')}
            </h3>
            <p className="text-gray-600">
              {t('pages.schools.noSchools.message', 'Check back soon for upcoming educational programs.')}
            </p>
          </div>
        )}

        {/* View All Button */}
        {showViewAllButton && displayedSchools.length > 0 && (
          <div className="text-center">
            <Link
              to={viewAllButtonLink}
              className="inline-flex items-center justify-center bg-black text-white hover:bg-gray-800 px-8 py-4 rounded-lg transition-all duration-300 font-semibold hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span>{viewAllButtonText || t('pages.home.schools.viewAllSchools', 'View All Schools')}</span>
              <svg className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default SchoolsSection;
